import React, { useState, useEffect, useCallback, useRef } from "react";
import { usePublicAuth } from "../context/PublicAuthContext";
import { Navigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LogOut, User, Heart, Bell, BellOff, Save, Star, Loader2,
  Zap, Music, Edit3, ChevronRight, Check, X, Link2,
  Download, Trash2, ShieldAlert, AlertTriangle, Settings, Disc3, Mail, GripVertical, CreditCard
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../utils/api";
import ImageUploadField from "../components/ImageUploadField";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/84f2e1dc-413f-4a02-b5b8-0fa677630a0d/images/ce3832daa6940f54bfb706607332b3df16b09787826cb6f0aafeb123741be7d6.png";

function urlBase64ToUint8Array(b64) {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const base = (b64 + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base);
  return new Uint8Array([...raw].map(c => c.charCodeAt(0)));
}

/* ── Underline Tab ── */
function Tab({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative pb-4 text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
        active ? "text-white" : "text-zinc-500 hover:text-zinc-300"
      }`}
    >
      {children}
      {active && (
        <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-rose-500 rounded-t-full shadow-[0_-2px_8px_rgba(225,29,72,0.5)]" />
      )}
    </button>
  );
}

/* ── Toggle ── */
function Toggle({ checked, onChange, disabled, testId }) {
  return (
    <button onClick={() => onChange(!checked)} disabled={disabled} data-testid={testId}
      className={`relative w-10 h-5.5 rounded-full transition-all duration-200 flex-shrink-0 ${checked ? "bg-rose-600 shadow-[0_0_12px_rgba(225,29,72,0.35)]" : "bg-zinc-800"}`}
      style={{ height: "22px", width: "40px" }}
    >
      <span className={`absolute top-[3px] left-[3px] w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${checked ? "translate-x-[18px]" : "translate-x-0"}`} />
    </button>
  );
}

/* ── Artist Application Form ── */
function ArtistApplicationForm({ publicApi }) {
  const [artistName, setArtistName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [infoText, setInfoText] = useState("");
  const [artistSearch, setArtistSearch] = useState([]);
  const [selectedArtistId, setSelectedArtistId] = useState(null);
  const [selectedArtistName, setSelectedArtistName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [myApps, setMyApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(true);

  useEffect(() => {
    publicApi("get", "/artist-applications/my")
      .then(r => setMyApps(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingApps(false));
  }, [submitted]); // eslint-disable-line

  const searchArtists = useCallback(async (q) => {
    if (!q.trim()) { setArtistSearch([]); return; }
    try {
      const r = await publicApi("get", `/artists/?search=${encodeURIComponent(q)}&limit=6`);
      setArtistSearch(r.data?.artists || []);
    } catch { /* silent */ }
  }, [publicApi]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!artistName.trim() || !contactEmail.trim() || !infoText.trim()) {
      setError("Bitte alle Felder ausfüllen."); return;
    }
    setSubmitting(true); setError("");
    try {
      await publicApi("post", "/artist-applications", {
        artist_name: artistName,
        contact_email: contactEmail,
        info_text: infoText,
        existing_artist_id: selectedArtistId || undefined,
      });
      setSubmitted(true);
      setArtistName(""); setContactEmail(""); setInfoText(""); setSelectedArtistId(null); setSelectedArtistName("");
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler beim Einreichen");
    } finally { setSubmitting(false); }
  };

  const latestApp = myApps[0];
  const statusColor = { pending: "text-amber-400", approved: "text-emerald-400", rejected: "text-zinc-500" };
  const statusLabel = { pending: "Ausstehend – wird geprüft", approved: "Genehmigt!", rejected: "Abgelehnt" };

  return (
    <div className="space-y-5">
      {/* Bisherige Bewerbungen */}
      {!loadingApps && myApps.length > 0 && (
        <div className="bg-zinc-900/40 border border-white/[0.05] rounded-2xl px-5 py-4 space-y-2">
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-600 mb-3">Meine Bewerbungen</p>
          {myApps.map(app => (
            <div key={app.id} className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-zinc-300 font-semibold">{app.artist_name}</p>
                <p className="text-xs text-zinc-600">{new Date(app.created_at).toLocaleDateString("de")}</p>
              </div>
              <span className={`text-xs font-bold ${statusColor[app.status]}`}>{statusLabel[app.status]}</span>
            </div>
          ))}
          {latestApp?.reviewer_note && (
            <p className="text-xs text-zinc-600 italic pt-1 border-t border-white/[0.04]">Notiz: {latestApp.reviewer_note}</p>
          )}
        </div>
      )}

      {/* Formular */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-zinc-400 leading-relaxed">
          Bewirbst du dich als Artist? Gib hier an, mit welchem Artist-Profil du verknüpft werden möchtest.
          Wir prüfen deine Bewerbung und schalten das Artist-Badge frei.
        </p>

        {/* Artist-Suche */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 ml-1">
            Artist-Name *
          </label>
          <input
            value={artistName}
            onChange={e => { setArtistName(e.target.value); searchArtists(e.target.value); setSelectedArtistId(null); setSelectedArtistName(""); }}
            placeholder="Dein Künstlername"
            required
            data-testid="application-artist-name"
            className="w-full bg-zinc-900/60 text-zinc-200 rounded-2xl px-4 py-3 text-sm ring-1 ring-inset ring-white/[0.06] focus:ring-rose-500/40 focus:outline-none"
          />
          {artistSearch.length > 0 && (
            <div className="mt-1 bg-zinc-900 border border-white/[0.06] rounded-xl overflow-hidden">
              {artistSearch.map(a => (
                <button key={a.id} type="button"
                  onClick={() => { setSelectedArtistId(a.id); setSelectedArtistName(a.name); setArtistName(a.name); setArtistSearch([]); }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-zinc-800 transition-colors flex items-center gap-2 ${selectedArtistId === a.id ? "text-emerald-400" : "text-zinc-300"}`}>
                  {selectedArtistId === a.id && <Check size={12} />}
                  {a.name}
                  <span className="text-zinc-600 text-xs ml-auto">Bestehend</span>
                </button>
              ))}
            </div>
          )}
          {selectedArtistId && (
            <p className="text-xs text-emerald-500 mt-1 ml-1">Bestehendes Profil ausgewählt: {selectedArtistName}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 ml-1">Kontakt-E-Mail *</label>
          <input
            value={contactEmail}
            onChange={e => setContactEmail(e.target.value)}
            type="email"
            placeholder="deine@email.de"
            required
            data-testid="application-contact-email"
            className="w-full bg-zinc-900/60 text-zinc-200 rounded-2xl px-4 py-3 text-sm ring-1 ring-inset ring-white/[0.06] focus:ring-rose-500/40 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 ml-1">Warum bist du dieser Artist? *</label>
          <textarea
            value={infoText}
            onChange={e => setInfoText(e.target.value.slice(0, 1000))}
            rows={4}
            placeholder="Erkläre kurz wer du bist, welche Releases du hast und wie wir dich verifizieren können…"
            required
            data-testid="application-info-text"
            className="w-full bg-zinc-900/60 text-zinc-200 rounded-2xl px-4 py-3 text-sm ring-1 ring-inset ring-white/[0.06] focus:ring-rose-500/40 focus:outline-none resize-none"
          />
          <p className="text-zinc-700 text-xs mt-1 ml-1">{infoText.length}/1000</p>
        </div>

        {error && <p className="text-red-400 text-sm">{error}</p>}
        {submitted && <p className="text-emerald-400 text-sm flex items-center gap-1.5"><Check size={13} /> Bewerbung eingereicht! Wir melden uns.</p>}

        <button
          type="submit"
          disabled={submitting}
          data-testid="application-submit-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-rose-600 hover:bg-rose-500 text-white font-bold text-sm transition-colors disabled:opacity-60"
        >
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Music size={14} />}
          Bewerbung einreichen
        </button>
      </form>
    </div>
  );
}

import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";

/* ── Sortable Artist Pill ── */
function SortableArtistPill({ id, name, isSelected, isPrimary, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} className={`relative flex-shrink-0 ${isDragging ? "opacity-80" : ""}`}>
      <button
        onClick={onClick}
        className={`flex items-center gap-1.5 pl-2 pr-3 py-1.5 rounded-full text-sm font-semibold transition-all select-none ${
          isSelected
            ? "bg-rose-600 text-white shadow-[0_0_12px_rgba(225,29,72,0.3)]"
            : "bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700"
        }`}
      >
        {/* Drag Handle */}
        <span
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-current opacity-40 hover:opacity-80 transition-opacity"
          title="Ziehen zum Sortieren"
          onClick={e => e.stopPropagation()}
        >
          <GripVertical size={12} />
        </span>
        {name}
        {isPrimary && (
          <span className="text-[8px] font-black uppercase tracking-wider opacity-70 ml-0.5">Haupt</span>
        )}
      </button>
    </div>
  );
}

const genitive = (name) =>
  name && (name.endsWith("s") || name.endsWith("x") || name.endsWith("z"))
    ? `${name}'`
    : `${name}s`;

/* ── Artist Studio Tab ── */
function ArtistStudioTab({ publicUser, publicApi, setPublicUser }) {
  const artistIds = publicUser?.artist_ids || (publicUser?.artist_id ? [publicUser.artist_id] : []);

  const [selectedArtistId, setSelectedArtistId] = useState(artistIds[0] || null);
  const [artistsMap, setArtistsMap] = useState({});  // { artistId: artistData }
  const [bio, setBio] = useState("");
  const [pendingBio, setPendingBio] = useState(null);
  const [pendingChanges, setPendingChanges] = useState(null);
  const [topTracks, setTopTracks] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Erweiterte Artist-Felder
  const [artistBildUrl, setArtistBildUrl] = useState("");
  const [artistWohnort, setArtistWohnort] = useState("");
  const [artistSocial, setArtistSocial] = useState({});

  // Alle Artists laden
  useEffect(() => {
    if (!artistIds.length) return;
    Promise.all(
      artistIds.map(id => api.get(`/artists/${id}`).then(r => [id, r.data]).catch(() => null))
    ).then(results => {
      const map = {};
      results.filter(Boolean).forEach(([id, data]) => { map[id] = data; });
      setArtistsMap(map);
    });
  }, [artistIds.join(",")]); // eslint-disable-line

  // Wenn artist wechselt, alle Felder laden
  useEffect(() => {
    if (!selectedArtistId || !artistsMap[selectedArtistId]) return;
    const a = artistsMap[selectedArtistId];
    setBio(a.biografie || "");
    setPendingBio(a.pending_bio || null);
    setPendingChanges(a.pending_changes || null);
    setArtistBildUrl(a.bild_url || "");
    setArtistWohnort(a.wohnort || "");
    setArtistSocial(a.social_links || {});
    setTopTracks([]);
    if (a.top_track_ids?.length) {
      Promise.all(a.top_track_ids.map(id => api.get(`/releases/by-id/${id}`).then(x => x.data).catch(() => null)))
        .then(tracks => setTopTracks(tracks.filter(Boolean)));
    }
  }, [selectedArtistId, artistsMap]);

  // Sortier-State für Artist-Reihenfolge
  const [sortedArtistIds, setSortedArtistIds] = useState(artistIds);
  const [orderSaving, setOrderSaving] = useState(false);
  const [orderSaved, setOrderSaved] = useState(false);

  // Sync wenn artistIds sich ändern (z.B. nach erstem Laden)
  useEffect(() => {
    setSortedArtistIds(artistIds);
  }, [artistIds.join(",")]); // eslint-disable-line

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sortedArtistIds.indexOf(active.id);
    const newIndex = sortedArtistIds.indexOf(over.id);
    const newOrder = arrayMove(sortedArtistIds, oldIndex, newIndex);
    setSortedArtistIds(newOrder);
    // Primary wechselt automatisch wenn erster Platz sich ändert
    if (newOrder[0] !== sortedArtistIds[0]) {
      setSelectedArtistId(newOrder[0]);
    }
    // Backend speichern
    setOrderSaving(true);
    try {
      await publicApi("put", "/me/artist-order", { artist_ids: newOrder });
      // Context aktualisieren damit Header-Badges sofort die neue Reihenfolge zeigen
      setPublicUser(prev => ({ ...prev, artist_ids: newOrder, artist_id: newOrder[0] }));
      setOrderSaved(true);
      setTimeout(() => setOrderSaved(false), 3000);
    } catch {} finally { setOrderSaving(false); }
  };

  const artistData = artistsMap[selectedArtistId] || null;

  const handleSearch = useCallback(async (q) => {
    if (!q.trim()) { setSearchResults([]); return; }
    try {
      const res = await api.get(`/releases/?search=${encodeURIComponent(q)}&limit=10`);
      setSearchResults(res.data?.items || []);
    } catch {}
  }, []);

  useEffect(() => {
    const t = setTimeout(() => handleSearch(searchQ), 300);
    return () => clearTimeout(t);
  }, [searchQ, handleSearch]);

  const addTrack = (track) => {
    if (topTracks.length >= 5 || topTracks.find(t => t.id === track.id)) return;
    setTopTracks(prev => [...prev, track]);
    setSearchQ(""); setSearchResults([]);
  };
  const removeTrack = (id) => setTopTracks(prev => prev.filter(t => t.id !== id));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await publicApi("put", "/me/artist-profile", {
        bio,
        top_track_ids: topTracks.map(t => t.id),
        target_artist_id: selectedArtistId,
        bild_url: artistBildUrl || null,
        social_links: Object.keys(artistSocial).length ? artistSocial : null,
        wohnort: artistWohnort || null,
      });
      if (res.data?.bio_pending_review) setPendingBio(res.data.pending_bio);
      if (res.data?.changes_pending_review) setPendingChanges(true);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {} finally { setSaving(false); }
  };

  if (!artistIds.length) return (
    <div>
      <div className="flex items-center gap-2 mb-5 pb-4 border-b border-white/[0.05]">
        <Music size={14} className="text-zinc-500" />
        <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">Artist-Bewerbung</p>
      </div>
      <ArtistApplicationForm publicApi={publicApi} />
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-2xl bg-rose-600/10 flex items-center justify-center flex-shrink-0">
          <Music size={16} className="text-rose-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold">Artist Studio</p>
          <p className="text-zinc-500 text-xs">
            {artistIds.length > 1
              ? <>{artistIds.length} Artists verknüpft &middot; <span className="text-rose-400 font-medium">{artistsMap[artistIds[0]]?.name || publicUser.artist_name}</span> <span className="text-zinc-700">(Haupt)</span></>
              : <>Verknüpft mit: <span className="text-rose-400 font-medium">{artistData?.name || publicUser.artist_name}</span></>
            }
          </p>
        </div>
        {/* Links zu Artist-Profilen */}
        <div className="flex items-center gap-1 ml-auto">
          {artistIds.map(id => {
            const a = artistsMap[id];
            if (!a?.slug) return null;
            return (
              <Link key={id} to={`/artist/${a.slug}`}
                title={a.name}
                className="flex items-center gap-1 text-xs text-zinc-600 hover:text-rose-400 hover:bg-white/5 transition-colors p-2 rounded-xl">
                {a.name}
                <ChevronRight size={12} />
              </Link>
            );
          })}
        </div>
      </div>

      {/* Artist Selector – sortierbar per Drag & Drop */}
      {artistIds.length > 1 && (
        <div>
          <div className="flex items-center justify-between mb-2 ml-1">
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">
              Reihenfolge
            </span>
            <span className="text-xs text-zinc-600 flex items-center gap-1">
              <GripVertical size={10} /> Ziehen zum Sortieren · Erster = Haupt-Artist
              {orderSaving && <Loader2 size={10} className="animate-spin ml-1" />}
              {orderSaved && <Check size={10} className="text-emerald-400 ml-1" />}
            </span>
          </div>
          <DndContext
            sensors={dndSensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToHorizontalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={sortedArtistIds} strategy={horizontalListSortingStrategy}>
              <div className="flex gap-2 flex-wrap">
                {sortedArtistIds.map((id, idx) => {
                  const a = artistsMap[id];
                  return (
                    <SortableArtistPill
                      key={id}
                      id={id}
                      name={a?.name || id.slice(-6)}
                      isSelected={id === selectedArtistId}
                      isPrimary={idx === 0}
                      onClick={() => setSelectedArtistId(id)}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
          <p className="text-[10px] text-zinc-700 mt-2 ml-1">
            Der erste Artist wird als Haupt-Artist in der Community und im Profil-Header angezeigt.
          </p>
        </div>
      )}

      {/* Bio */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-3 ml-1">
          Artist Bio <span className="text-zinc-700 font-normal normal-case tracking-normal">({bio.length}/800)</span>
        </label>
        {pendingBio && (
          <div className="flex items-start gap-3 mb-3 px-4 py-3 rounded-xl bg-amber-500/8 border border-amber-500/20 text-amber-400 text-xs leading-relaxed">
            <span className="flex-shrink-0 mt-0.5">⏳</span>
            <div>
              <p className="font-bold mb-0.5">Bio wartet auf Genehmigung</p>
              <p className="text-amber-600/80">Deine neue Bio wird nach Admin-Freigabe veröffentlicht.</p>
            </div>
          </div>
        )}
        <textarea
          value={bio}
          onChange={e => setBio(e.target.value.slice(0, 800))}
          rows={5}
          placeholder="Erzähl etwas über dich als Artist…"
          className="w-full bg-zinc-900/50 text-white rounded-2xl px-4 py-3.5 text-sm focus:outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/40 transition-all duration-200 placeholder:text-zinc-700 resize-none"
          data-testid="artist-bio-input"
        />
      </div>

      {/* Profilbild – URL oder Upload */}
      <ImageUploadField
        value={artistBildUrl}
        onChange={setArtistBildUrl}
        uploadEndpoint="/media/artist-image"
        oldPendingUrl={pendingChanges?.bild_url || ""}
        label="Profilbild (URL oder Upload)"
        testId="artist-bild"
      />

      {/* Wohnort */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 ml-1">Wohnort / Stadt</label>
        <input
          value={artistWohnort}
          onChange={e => setArtistWohnort(e.target.value)}
          placeholder="z.B. Berlin, Deutschland"
          className="w-full bg-zinc-900/50 text-white rounded-2xl px-4 py-3 text-sm focus:outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/40 transition-all duration-200 placeholder:text-zinc-700"
          data-testid="artist-wohnort-input"
        />
      </div>

      {/* Social Links */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-3 ml-1">Social Media Links</label>
        <div className="space-y-2">
          {[
            { key: "instagram", label: "Instagram", ph: "https://instagram.com/..." },
            { key: "facebook", label: "Facebook", ph: "https://facebook.com/..." },
            { key: "soundcloud", label: "SoundCloud", ph: "https://soundcloud.com/..." },
            { key: "beatport", label: "Beatport", ph: "https://www.beatport.com/artist/..." },
            { key: "spotify", label: "Spotify", ph: "https://open.spotify.com/artist/..." },
            { key: "youtube", label: "YouTube", ph: "https://youtube.com/@..." },
            { key: "tiktok", label: "TikTok", ph: "https://tiktok.com/@..." },
            { key: "twitch", label: "Twitch", ph: "https://twitch.tv/..." },
          ].map(({ key, label, ph }) => (
            <div key={key} className="grid items-center gap-2" style={{ gridTemplateColumns: "5.5rem 1fr" }}>
              <span className="text-zinc-500 text-xs font-medium text-right pr-1">{label}</span>
              <input
                value={artistSocial[key] || ""}
                onChange={e => setArtistSocial(prev => ({ ...prev, [key]: e.target.value }))}
                placeholder={ph}
                className="bg-zinc-900/50 text-white rounded-xl px-3 py-2 text-xs focus:outline-none ring-1 ring-inset ring-white/[0.06] focus:ring-rose-500/40 transition-all placeholder:text-zinc-700"
              />
            </div>
          ))}
        </div>
      </div>

      {pendingChanges && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-500/8 border border-amber-500/20 text-amber-400 text-xs leading-relaxed">
          <span className="flex-shrink-0 mt-0.5">⏳</span>
          <div>
            <p className="font-bold mb-0.5">Profiländerungen warten auf Genehmigung</p>
            <p className="text-amber-600/80">Deine Änderungen werden nach Admin-Freigabe übernommen.</p>
          </div>
        </div>
      )}

      {/* Top 5 Tracks */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-3 ml-1">
          {artistData?.name ? `${genitive(artistData.name)} persönliche Top 5` : "Persönliche Top 5"} <span className="text-zinc-700 font-normal normal-case tracking-normal">({topTracks.length}/5)</span>
        </label>
        <div className="space-y-2 mb-2">
          {topTracks.map((t, i) => (
            <div key={t.id} className="flex items-center gap-3 p-3 rounded-2xl bg-zinc-900/40 group shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]">
              <span className="text-zinc-700 font-bold text-xs w-4 text-center flex-shrink-0">{i + 1}</span>
              {t.cover_url && <img src={t.cover_url} alt={t.titel} className="w-9 h-9 rounded-xl object-cover flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{t.interpret}</p>
                <p className="text-zinc-500 text-xs truncate">{t.titel}</p>
              </div>
              <button onClick={() => removeTrack(t.id)} className="text-zinc-700 hover:text-red-400 transition-colors p-1.5 hover:bg-red-500/10 rounded-lg opacity-0 group-hover:opacity-100">
                <X size={13} />
              </button>
            </div>
          ))}
          {topTracks.length < 5 && (
            <div className="relative">
              <input
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                placeholder="Release suchen und hinzufügen…"
                className="w-full bg-zinc-900/30 rounded-2xl px-4 py-3 text-sm text-white outline-none ring-1 ring-inset ring-dashed ring-white/[0.08] focus:ring-rose-500/30 placeholder:text-zinc-700 transition-all duration-200"
                data-testid="top-tracks-search"
              />
              {searchResults.length > 0 && (
                <div className="absolute top-full mt-2 left-0 right-0 z-50 bg-zinc-950/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_16px_48px_rgba(0,0,0,0.5)] max-h-72 overflow-y-auto">
                  {searchResults.map(r => (
                    <button key={r.id} onClick={() => addTrack(r)}
                      className="w-full flex items-center gap-3 text-left px-4 py-2.5 text-sm text-zinc-300 hover:bg-white/[0.06] hover:text-white transition-colors first:rounded-t-2xl last:rounded-b-2xl">
                      {r.cover_url && <img src={r.cover_url} alt={r.titel} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />}
                      <span className="truncate">{r.interpret} – {r.titel}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving} data-testid="artist-profile-save-btn"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-rose-600 text-white text-sm font-medium shadow-[inset_0_1px_0_rgba(255,255,255,0.15),0_4px_12px_rgba(225,29,72,0.2)] hover:bg-rose-500 hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(225,29,72,0.3)] disabled:opacity-50 transition-all duration-200">
          {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <Check size={14} /> : <Save size={14} />}
          {saving ? "Speichern…" : saved ? "Gespeichert!" : "Änderungen speichern"}
        </button>
      </div>
    </div>
  );
}

/* ── Main ── */
export default function UserProfilePage() {
  const { publicUser, isPublicAuth, isPublicLoading, logoutPublic, publicApi, setPublicUser } = usePublicAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("profile");

  const [username, setUsername] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [bio, setBioField] = useState("");
  const [location, setLocation] = useState("");
  const [genre, setGenre] = useState("");
  const [favArtistsText, setFavArtistsText] = useState("");
  const [socialLinks, setSocialLinks] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelDone, setCancelDone] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [subLoading, setSubLoading] = useState(false);

  // DSGVO
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Password Reset
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState("");

  // Email ändern
  const [emailEdit, setEmailEdit] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);
  const [emailError, setEmailError] = useState("");

  // Passwort ändern
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  // Datenschutz
  const [profilPublic, setProfilPublic] = useState(publicUser?.profil_public || false);
  const [inMemberList, setInMemberList] = useState(publicUser?.in_member_list || false);
  const [privacySaving, setPrivacySaving] = useState(false);
  const [privacySaved, setPrivacySaved] = useState(false);

  // Notification-Prefs
  const defaultPrefs = { new_release: true, new_follower: true, push_new_release: true };
  const [notifPrefs, setNotifPrefs] = useState({ ...defaultPrefs, ...(publicUser?.notification_prefs || {}) });
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifSaved, setNotifSaved] = useState(false);

  // Privacy aus publicUser synchronisieren
  useEffect(() => {
    if (publicUser) {
      setProfilPublic(publicUser.profil_public || false);
      setInMemberList(publicUser.in_member_list || false);
    }
  }, [publicUser?.profil_public, publicUser?.in_member_list]); // eslint-disable-line react-hooks/exhaustive-deps

  // Push
  const [pushStatus, setPushStatus] = useState(null);
  const [pushLoading, setPushLoading] = useState(false);
  const [emailNotif, setEmailNotif] = useState(false);

  useEffect(() => {
    if (publicUser?.username) setUsername(publicUser.username);
    if (publicUser?.avatar_url) setAvatarUrl(publicUser.avatar_url);
    if (publicUser?.bio !== undefined) setBioField(publicUser.bio || "");
    if (publicUser?.location !== undefined) setLocation(publicUser.location || "");
    if (publicUser?.genre !== undefined) setGenre(publicUser.genre || "");
    if (publicUser?.fav_artists_text !== undefined) setFavArtistsText(publicUser.fav_artists_text || "");
    if (publicUser?.social_links) setSocialLinks(publicUser.social_links || {});
    if (publicUser?.email !== undefined) setEmailEdit(publicUser.email || "");
  }, [publicUser?.username, publicUser?.avatar_url, publicUser?.bio, publicUser?.location, publicUser?.genre, publicUser?.fav_artists_text, publicUser?.social_links, publicUser?.email]); // eslint-disable-line

  useEffect(() => {
    if (!isPublicAuth || !publicUser) return;
    publicApi("get", "/push/status")
      .then(res => { setPushStatus(res.data); setEmailNotif(res.data.email_notifications); })
      .catch(() => {});
  }, [isPublicAuth, publicUser, publicApi]);

  const handlePushToggle = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    setPushLoading(true);
    try {
      if (pushStatus?.subscribed) {
        await publicApi("delete", "/push/subscribe");
        setPushStatus({ subscribed: false, email_notifications: false });
      } else {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") return;
        const reg = await navigator.serviceWorker.ready;
        const vapidRes = await fetch(`${BACKEND_URL}/api/push/vapid-public-key`);
        if (!vapidRes.ok) throw new Error("VAPID-Key nicht verfügbar");
        const { public_key } = await vapidRes.json();
        if (!public_key) throw new Error("VAPID-Key fehlt in Server-Konfiguration");
        const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(public_key) });
        await publicApi("post", "/push/subscribe", { subscription: sub.toJSON(), email_notifications: emailNotif });
        setPushStatus({ subscribed: true, email_notifications: emailNotif });
      }
    } finally { setPushLoading(false); }
  }, [pushStatus, emailNotif, publicApi]);

  const handleEmailNotifToggle = async (val) => {
    setEmailNotif(val);
    if (pushStatus?.subscribed) {
      await publicApi("post", "/push/subscribe", { subscription: {}, email_notifications: val }).catch(() => {});
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setSaveError(""); setSaved(false);
    try {
      const res = await publicApi("put", "/me/me", {
        username,
        avatar_url: avatarUrl || null,
        bio: bio || null,
        location: location || null,
        genre: genre || null,
        fav_artists_text: favArtistsText || null,
        social_links: socialLinks,
      });
      setPublicUser(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveError(err.response?.data?.detail || "Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  const handlePrivacySave = async () => {
    setPrivacySaving(true); setPrivacySaved(false);
    try {
      const res = await publicApi("put", "/me/me", {
        profil_public: profilPublic,
        in_member_list: profilPublic && inMemberList,
      });
      setPublicUser(res.data);
      setPrivacySaved(true);
      setTimeout(() => setPrivacySaved(false), 3000);
    } catch { /* silent */ } finally { setPrivacySaving(false); }
  };

  const handleNotifSave = async () => {
    setNotifSaving(true); setNotifSaved(false);
    try {
      await publicApi("put", "/me/me", { notification_prefs: notifPrefs });
      setNotifSaved(true);
      setTimeout(() => setNotifSaved(false), 3000);
    } catch { /* silent */ } finally { setNotifSaving(false); }
  };

  const handlePasswordReset = async () => {
    setResetLoading(true); setResetError(""); setResetSent(false);
    try {
      await publicApi("post", "/me/request-password-reset");
      setResetSent(true);
      setTimeout(() => setResetSent(false), 6000);
    } catch (err) {
      setResetError(err.response?.data?.detail || "Fehler beim Senden der Reset-Mail");
    } finally { setResetLoading(false); }
  };

  const handleEmailSave = async () => {
    setEmailSaving(true); setEmailError(""); setEmailSaved(false);
    try {
      await publicApi("put", "/me/email", { email: emailEdit });
      setEmailSaved(true);
      setTimeout(() => setEmailSaved(false), 6000);
    } catch (err) {
      setEmailError(err.response?.data?.detail || "Fehler beim Aktualisieren der E-Mail");
    } finally { setEmailSaving(false); }
  };

  const handlePasswordSave = async () => {
    setPasswordError("");
    if (newPassword !== confirmPassword) { setPasswordError("Passwörter stimmen nicht überein"); return; }
    if (newPassword.length < 8) { setPasswordError("Passwort muss mindestens 8 Zeichen lang sein"); return; }
    setPasswordSaving(true); setPasswordSaved(false);
    try {
      await publicApi("put", "/me/password", { password: newPassword });
      setPasswordSaved(true);
      setNewPassword(""); setConfirmPassword(""); setShowPasswordForm(false);
      setTimeout(() => setPasswordSaved(false), 5000);
    } catch (err) {
      setPasswordError(err.response?.data?.detail || "Fehler beim Ändern des Passworts");
    } finally { setPasswordSaving(false); }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Abo wirklich zum Periodenende kündigen?")) return;
    setCancelLoading(true);
    try {
      await publicApi("delete", "/subscriptions/cancel");
      setCancelDone(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Kündigen");
    } finally { setCancelLoading(false); }
  };

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const res = await publicApi("get", "/me/export");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "huwnd-daten-export.json"; a.click();
      URL.revokeObjectURL(url);
    } catch { alert("Export fehlgeschlagen."); }
    finally { setExportLoading(false); }
  };

  const handleDeleteAccount = async () => {
    if (!deleteConfirmed) return;
    setDeleteLoading(true);
    try {
      await publicApi("delete", "/me/account");
      logoutPublic();
    } catch {
      alert("Löschen fehlgeschlagen. Bitte kontaktiere support@huwnd.de");
      setDeleteLoading(false);
    }
  };

  const isSupporter = publicUser?.is_supporter;

  // Stripe-Status laden wenn Supporter-Tab aktiv – muss VOR allen Early Returns stehen
  useEffect(() => {
    if (activeTab !== "supporter" || !isSupporter || subStatus) return;
    setSubLoading(true);
    Promise.all([
      publicApi("get", "/subscriptions/my-status"),
      publicApi("get", "/subscriptions/my-invoices"),
    ]).then(([s, i]) => {
      setSubStatus(s?.data || null);
      setInvoices(i?.data?.invoices || []);
    }).catch(() => {}).finally(() => setSubLoading(false));
  }, [activeTab, isSupporter]); // eslint-disable-line

  if (isPublicLoading) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <Loader2 className="animate-spin text-rose-500" size={28} />
    </div>
  );
  if (!isPublicAuth) return <Navigate to="/login" replace />;
  const isLinkedArtist = Boolean(publicUser?.artist_id);
  const initials = (publicUser?.username || "?").slice(0, 2).toUpperCase();

  const tabs = [
    { id: "profile", label: t("profile.tab_profile") },
    ...(isSupporter ? [{ id: "supporter", label: t("profile.tab_supporter") }] : []),
    { id: "datenschutz", label: t("profile.tab_privacy") },
  ];

  return (
    <>
      <div className="min-h-screen bg-[#080808] text-white">
        <Header />

        {/* ── HERO ── */}
        <div className="relative h-52 sm:h-64 overflow-hidden">
          <img
            src={HERO_BG}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#080808]/20 to-transparent" />
        </div>

        {/* ── PROFILE HEADER ── */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 -mt-16 relative z-10">
          <div className="flex items-end gap-5 mb-6">
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              <div className={`w-24 h-24 sm:w-28 sm:h-28 rounded-3xl overflow-hidden shadow-[0_12px_40px_rgba(0,0,0,0.7)] ring-4 ${isLinkedArtist ? "ring-blue-500/40" : isSupporter ? "ring-rose-500/40" : "ring-zinc-800"}`}>
                {publicUser?.avatar_url ? (
                  <img src={publicUser.avatar_url} alt={publicUser.username} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-zinc-700 to-zinc-900 flex items-center justify-center text-3xl font-black text-white/80">
                    {initials}
                  </div>
                )}
              </div>
              {/* Supporter Star – immer rechts unten */}
              {isSupporter && (
                <div className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-2xl bg-rose-600 flex items-center justify-center ring-4 ring-[#080808] shadow-[0_0_16px_rgba(225,29,72,0.5)]">
                  <Star size={14} className="text-white fill-white" />
                </div>
              )}
              {/* Artist Vinyl – rechts wenn allein, links wenn Supporter auch aktiv */}
              {isLinkedArtist && !isSupporter && (
                <div className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-2xl bg-blue-600 flex items-center justify-center ring-4 ring-[#080808] shadow-[0_0_16px_rgba(59,130,246,0.4)]">
                  <Disc3 size={14} className="text-white" />
                </div>
              )}
              {isLinkedArtist && isSupporter && (
                <div className="absolute -bottom-1.5 -left-1.5 w-8 h-8 rounded-2xl bg-blue-600 flex items-center justify-center ring-4 ring-[#080808] shadow-[0_0_16px_rgba(59,130,246,0.4)]">
                  <Disc3 size={14} className="text-white" />
                </div>
              )}
            </div>

            {/* Name + badges */}
            <div className="flex-1 min-w-0 pb-1">
              <div className="flex items-center gap-2.5 flex-wrap mb-1">
                <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-white tracking-tight truncate">
                  {publicUser?.username}
                </h1>
                {isSupporter && (
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20 backdrop-blur uppercase tracking-wider flex-shrink-0">
                    <Star size={8} className="fill-rose-400" /> Supporter
                  </span>
                )}
                {/* Alle Artists: Haupt + Weitere */}
                {isLinkedArtist && (publicUser?.artist_ids || [publicUser?.artist_id]).filter(Boolean).map((aid, idx) => {
                  const info = publicUser?.artist_slugs?.[aid];
                  const name = info?.name || (idx === 0 ? publicUser.artist_name : aid.slice(-5));
                  const slug = info?.slug || publicUser?.artist_slug;
                  const isPrimary = idx === 0;
                  return (
                    <Link key={aid} to={`/artist/${slug || aid}`}
                      className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider transition-colors flex-shrink-0 ${
                        isPrimary
                          ? "bg-blue-600 text-white hover:bg-blue-500"
                          : "bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20"
                      }`}>
                      <Disc3 size={8} /> {name}{isPrimary && (publicUser?.artist_ids?.length > 1) && <span className="opacity-60 ml-0.5 font-medium normal-case tracking-normal">Haupt</span>}
                    </Link>
                  );
                })}
              </div>
              <p className="text-zinc-600 text-sm">{publicUser?.email}</p>
            </div>

            {/* Logout button */}
            <button onClick={logoutPublic} data-testid="public-logout-btn"
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-xl text-zinc-600 hover:text-zinc-300 hover:bg-white/5 text-sm transition-all duration-200 flex-shrink-0 mb-1">
              <LogOut size={14} /> Abmelden
            </button>
          </div>

          {/* ── TABS ── */}
          <div className="flex items-center gap-7 border-b border-white/[0.07] mb-8">
            {tabs.map(t => (
              <Tab key={t.id} active={activeTab === t.id} onClick={() => setActiveTab(t.id)} testId={`tab-${t.id}`}>
                {t.label}
              </Tab>
            ))}
          </div>

          {/* ── PROFIL TAB ── */}
          {activeTab === "profile" && (
            <div className="space-y-6 pb-16">
              <form onSubmit={handleSave} className="space-y-6">
                {saveError && (
                  <p className="text-red-400 text-sm bg-red-500/5 rounded-2xl px-4 py-3 ring-1 ring-inset ring-red-500/20">{saveError}</p>
                )}

                <div className="space-y-5">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 ml-1">Benutzername</label>
                    <input
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      className="w-full bg-zinc-900/50 text-white rounded-2xl px-4 py-3.5 text-sm focus:outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/40 focus:bg-zinc-900/80 transition-all duration-200"
                      required
                      data-testid="public-profile-username"
                    />
                  </div>
                  <div>
                    <ImageUploadField
                      value={avatarUrl}
                      onChange={setAvatarUrl}
                      uploadEndpoint="/media/user-avatar"
                      oldPendingUrl=""
                      label={`Profilbild (optional)`}
                      testId="public-profile-avatar"
                    />
                    <p className="text-zinc-700 text-xs mt-2 ml-1">{t("profile.avatar_hint")}</p>
                  </div>

                  {/* Kurztext-Bio */}
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 ml-1">{t("profile.bio_label")} <span className="text-zinc-700 font-normal normal-case">({bio.length}/300)</span></label>
                    <textarea
                      value={bio}
                      onChange={e => setBioField(e.target.value.slice(0, 300))}
                      rows={3}
                      placeholder="Kurze Vorstellung…"
                      data-testid="profile-bio-input"
                      className="w-full bg-zinc-900/60 text-zinc-200 rounded-2xl px-4 py-3 text-sm ring-1 ring-inset ring-white/[0.06] focus:ring-rose-500/40 focus:outline-none resize-none"
                    />
                  </div>

                  {/* Standort & Genre */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 ml-1">{t("profile.location_label")}</label>
                      <input
                        value={location}
                        onChange={e => setLocation(e.target.value.slice(0, 80))}
                        placeholder={t("profile.location_placeholder")}
                        data-testid="profile-location-input"
                        className="w-full bg-zinc-900/60 text-zinc-200 rounded-2xl px-4 py-3 text-sm ring-1 ring-inset ring-white/[0.06] focus:ring-rose-500/40 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 ml-1">{t("profile.genre_label")}</label>
                      <input
                        value={genre}
                        onChange={e => setGenre(e.target.value.slice(0, 80))}
                        placeholder={t("profile.genre_placeholder")}
                        data-testid="profile-genre-input"
                        className="w-full bg-zinc-900/60 text-zinc-200 rounded-2xl px-4 py-3 text-sm ring-1 ring-inset ring-white/[0.06] focus:ring-rose-500/40 focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* Lieblingskünstler */}
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 ml-1">{t("profile.fav_artists_label")}</label>
                    <input
                      value={favArtistsText}
                      onChange={e => setFavArtistsText(e.target.value.slice(0, 200))}
                      placeholder={t("profile.fav_artists_placeholder")}
                      data-testid="profile-fav-artists-input"
                      className="w-full bg-zinc-900/60 text-zinc-200 rounded-2xl px-4 py-3 text-sm ring-1 ring-inset ring-white/[0.06] focus:ring-rose-500/40 focus:outline-none"
                    />
                  </div>

                  {/* Social Links */}
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 ml-1">{t("profile.social_links_label")}</label>
                    <div className="space-y-2">
                      {["instagram", "tiktok", "youtube", "soundcloud"].map(platform => (
                        <div key={platform} className="flex items-center gap-2">
                          <span className="text-zinc-600 text-xs w-24 capitalize flex-shrink-0">{platform}</span>
                          <input
                            value={socialLinks[platform] || ""}
                            onChange={e => setSocialLinks(prev => ({ ...prev, [platform]: e.target.value }))}
                            placeholder={`https://${platform}.com/…`}
                            data-testid={`social-${platform}-input`}
                            className="flex-1 bg-zinc-900/60 text-zinc-200 rounded-xl px-3 py-2 text-sm ring-1 ring-inset ring-white/[0.06] focus:ring-rose-500/40 focus:outline-none"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 ml-1">{t("profile.email_label")}</label>
                    {/* Nur auth0| Accounts können E-Mail/Passwort ändern */}
                    {publicUser?.auth0_id?.startsWith("auth0|") ? (
                      <div className="space-y-3">
                        {/* E-Mail editierbar */}
                        <div className="flex items-center gap-2">
                          <input
                            value={emailEdit}
                            onChange={e => setEmailEdit(e.target.value)}
                            type="email"
                            placeholder="deine@email.de"
                            data-testid="email-input"
                            className="flex-1 bg-zinc-900/50 text-white rounded-2xl px-4 py-3.5 text-sm focus:outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/40 focus:bg-zinc-900/80 transition-all duration-200"
                          />
                          <button
                            type="button"
                            onClick={handleEmailSave}
                            disabled={emailSaving || emailEdit === publicUser?.email}
                            data-testid="email-save-btn"
                            className="flex items-center gap-1.5 px-4 py-3.5 rounded-2xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-medium transition-colors disabled:opacity-40 flex-shrink-0"
                          >
                            {emailSaving ? <Loader2 size={14} className="animate-spin" /> : emailSaved ? <Check size={14} className="text-emerald-400" /> : <Save size={14} />}
                          </button>
                        </div>
                        {emailSaved && (
                          <p className="text-emerald-400 text-xs flex items-center gap-1 ml-1">
                            <Check size={11} /> {t("profile.email_saved")}
                          </p>
                        )}
                        {emailError && <p className="text-red-400 text-xs ml-1">{emailError}</p>}

                        {/* Passwort ändern */}
                        <div>
                          <button
                            type="button"
                            onClick={() => { setShowPasswordForm(p => !p); setPasswordError(""); setNewPassword(""); setConfirmPassword(""); }}
                            className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 text-xs transition-colors"
                          >
                            <Mail size={11} />
                            {showPasswordForm ? "Passwort-Formular schließen" : "Passwort ändern"}
                          </button>
                          {passwordSaved && <span className="ml-3 text-emerald-400 text-xs flex items-center gap-1 inline-flex"><Check size={11} /> Passwort geändert</span>}
                        </div>
                        {showPasswordForm && (
                          <div className="space-y-2 pt-1">
                            <input
                              type="password"
                              value={newPassword}
                              onChange={e => setNewPassword(e.target.value)}
                              placeholder="Neues Passwort (mind. 8 Zeichen)"
                              data-testid="new-password-input"
                              className="w-full bg-zinc-900/60 text-zinc-200 rounded-2xl px-4 py-3 text-sm ring-1 ring-inset ring-white/[0.06] focus:ring-rose-500/40 focus:outline-none"
                            />
                            <input
                              type="password"
                              value={confirmPassword}
                              onChange={e => setConfirmPassword(e.target.value)}
                              placeholder="Passwort bestätigen"
                              data-testid="confirm-password-input"
                              className="w-full bg-zinc-900/60 text-zinc-200 rounded-2xl px-4 py-3 text-sm ring-1 ring-inset ring-white/[0.06] focus:ring-rose-500/40 focus:outline-none"
                            />
                            {passwordError && <p className="text-red-400 text-xs ml-1">{passwordError}</p>}
                            <button
                              type="button"
                              onClick={handlePasswordSave}
                              disabled={passwordSaving || !newPassword || !confirmPassword}
                              data-testid="password-save-btn"
                              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-500 disabled:opacity-50 transition-colors"
                            >
                              {passwordSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                              Passwort speichern
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Social/Federated Login (Apple, Google etc.) – E-Mail read-only, kein Passwort */
                      <div className="space-y-2">
                        <div className="flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-zinc-900/30 ring-1 ring-inset ring-white/[0.04]">
                          <span className="text-zinc-400 text-sm flex-1 truncate">{publicUser?.email || "—"}</span>
                          <span className="text-zinc-700 text-xs flex-shrink-0">
                            {publicUser?.auth0_id?.startsWith("apple|") ? "Von Apple verwaltet" :
                             publicUser?.auth0_id?.startsWith("google-oauth2|") ? "Von Google verwaltet" :
                             "Vom Login-Anbieter verwaltet"}
                          </span>
                        </div>
                        <p className="text-zinc-600 text-xs ml-1">
                          {publicUser?.auth0_id?.startsWith("apple|")
                            ? "Du hast dich mit Apple angemeldet. E-Mail und Passwort werden von Apple verwaltet."
                            : "E-Mail und Passwort werden vom Login-Anbieter verwaltet."}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  {saved && (
                    <span className="flex items-center gap-1.5 text-emerald-400 text-sm">
                      <Check size={14} /> Gespeichert
                    </span>
                  )}
                  <div className="ml-auto">
                    <button type="submit" disabled={saving} data-testid="public-profile-save-btn"
                      className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-rose-600 text-white text-sm font-medium shadow-[inset_0_1px_0_rgba(255,255,255,0.15),0_4px_12px_rgba(225,29,72,0.2)] hover:bg-rose-500 hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(225,29,72,0.3)] disabled:opacity-50 transition-all duration-200">
                      {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                      {saving ? "Speichern…" : "Speichern"}
                    </button>
                  </div>
                </div>
              </form>

              {/* Supporter CTA */}
              {!isSupporter && (
                <Link to="/supporter"
                  className="group flex items-center gap-4 p-5 rounded-3xl transition-all duration-300 bg-zinc-900/30 hover:bg-zinc-900/50 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] overflow-hidden relative"
                  data-testid="supporter-upsell"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-rose-600/[0.04] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="w-10 h-10 rounded-2xl bg-rose-600/10 flex items-center justify-center flex-shrink-0 relative z-10">
                    <Zap size={16} className="text-rose-400" />
                  </div>
                  <div className="flex-1 relative z-10">
                    <p className="text-white text-sm font-semibold mb-0.5">{t("profile.supporter_upsell_title")}</p>
                    <p className="text-zinc-500 text-xs">{t("profile.supporter_upsell_desc")}</p>
                  </div>
                  <ChevronRight size={16} className="text-zinc-600 group-hover:text-zinc-300 group-hover:translate-x-0.5 transition-all flex-shrink-0 relative z-10" />
                </Link>
              )}

            </div>
          )}

          {/* ── SUPPORTER TAB ── */}
          {activeTab === "supporter" && isSupporter && (
            <div className="space-y-4 pb-16">

              {/* ── Stripe Abo-Verwaltung ── */}
              {subLoading && (
                <div className="flex items-center gap-2 px-5 py-4 rounded-2xl bg-zinc-900/40 border border-white/[0.05]">
                  <Loader2 size={14} className="animate-spin text-zinc-600" />
                  <span className="text-zinc-600 text-sm">{t("common.loading")}</span>
                </div>
              )}

              {!subLoading && (subStatus?.stripe_customer_id || publicUser?.stripe_customer_id) && (
                <div className="rounded-2xl border border-white/[0.07] overflow-hidden">
                  {/* Kündigung-Banner */}
                  {subStatus?.subscription?.cancel_at_period_end && subStatus?.subscription?.current_period_end && (
                    <div className="flex items-center gap-3 px-5 py-3.5 bg-amber-500/[0.07] border-b border-amber-500/20">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                      <p className="text-amber-300 text-sm">
                        {t("profile.cancellation_confirmed")}{" "}
                        <span className="font-bold">
                          {new Date(subStatus.subscription.current_period_end).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}
                        </span>
                      </p>
                    </div>
                  )}

                  {/* Status-Header */}
                  <div className="px-5 py-4 bg-zinc-900/60">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-rose-500/10 flex items-center justify-center flex-shrink-0">
                          <CreditCard size={14} className="text-rose-400" />
                        </div>
                        <div>
                          <p className="text-white text-sm font-bold">
                            {subStatus?.subscription?.status === "active" && !subStatus?.subscription?.cancel_at_period_end && t("profile.subscription_active")}
                            {subStatus?.subscription?.status === "active" && subStatus?.subscription?.cancel_at_period_end && t("profile.subscription_canceled")}
                            {subStatus?.subscription?.status === "past_due" && t("profile.subscription_past_due")}
                            {subStatus?.subscription?.status === "canceled" && t("profile.subscription_ended")}
                            {!subStatus?.subscription && t("profile.subscription_label")}
                          </p>
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            {(subStatus?.supporter_plan || publicUser?.supporter_plan) && (
                              <span className="text-zinc-500 text-xs">
                                {(subStatus?.supporter_plan || publicUser?.supporter_plan) === "yearly" ? t("common.yearly") : t("common.monthly")}
                              </span>
                            )}
                            {subStatus?.subscription?.current_period_end && !subStatus?.subscription?.cancel_at_period_end && (
                              <span className="text-zinc-600 text-xs">
                                {t("profile.next_payment")}:{" "}
                                <span className="text-zinc-400">{new Date(subStatus.subscription.current_period_end).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Portal-Button */}
                      <button
                        onClick={async () => {
                          setPortalLoading(true);
                          try {
                            const r = await publicApi("post", "/subscriptions/portal", { return_url: window.location.href });
                            window.location.href = r.data.url;
                          } catch { setPortalLoading(false); }
                        }}
                        disabled={portalLoading}
                        data-testid="manage-subscription-btn"
                        className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white text-xs font-semibold transition disabled:opacity-50"
                      >
                        {portalLoading ? <Loader2 size={11} className="animate-spin" /> : <CreditCard size={11} />}
                        {t("profile.manage_subscription")}
                      </button>
                    </div>
                  </div>

                  {/* Zahlungshistorie */}
                  {invoices.length > 0 && (
                    <div className="border-t border-white/[0.05]">
                      <p className="px-5 pt-4 pb-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-600">{t("profile.payment_history")}</p>
                      <div className="divide-y divide-white/[0.04]">
                        {invoices.slice(0, 5).map(inv => (
                          <div key={inv.id} className="flex items-center justify-between px-5 py-2.5">
                            <div className="flex items-center gap-3">
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${inv.paid ? "bg-emerald-500" : "bg-amber-500"}`} />
                              <span className="text-zinc-400 text-xs">{inv.created ? new Date(inv.created).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" }) : "–"}</span>
                              {inv.number && <span className="text-zinc-700 text-xs">{inv.number}</span>}
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-white text-xs font-semibold">{inv.amount_paid?.toFixed(2)} {inv.currency}</span>
                              {inv.hosted_invoice_url && (
                                <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                                  className="text-zinc-700 hover:text-zinc-400 transition text-[10px]">
                                  {t("profile.invoice_receipt")} ↗
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Kein Stripe-Konto → Support kontaktieren */}
              {!subLoading && isSupporter && !subStatus?.stripe_customer_id && !publicUser?.stripe_customer_id && (
                <div className="flex items-center justify-between px-5 py-4 rounded-2xl border border-white/[0.07] bg-zinc-900/60">
                  <div>
                    <p className="text-white text-sm font-semibold">Abonnement</p>
                    <p className="text-zinc-500 text-xs mt-0.5">Zur Verwaltung bitte Support kontaktieren</p>
                  </div>
                </div>
              )}

              {/* Push Notifications */}
              <div className="p-5 rounded-3xl bg-zinc-900/30 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-2xl flex items-center justify-center ${pushStatus?.subscribed ? "bg-rose-600/10" : "bg-zinc-800/60"}`}>
                      {pushStatus?.subscribed
                        ? <Bell size={15} className="text-rose-400" />
                        : <BellOff size={15} className="text-zinc-500" />}
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Push-Benachrichtigungen</p>
                      <p className="text-zinc-600 text-xs mt-0.5">Neue Releases von Artists die du folgst</p>
                    </div>
                  </div>
                  <Toggle checked={!!pushStatus?.subscribed} onChange={handlePushToggle} disabled={pushLoading} testId="push-toggle-btn" />
                </div>
                {pushStatus?.subscribed && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/[0.06]">
                    <span className="text-zinc-500 text-xs">Auch per E-Mail benachrichtigen</span>
                    <Toggle checked={emailNotif} onChange={handleEmailNotifToggle} testId="email-notif-toggle" />
                  </div>
                )}
              </div>

              {/* Hype */}
              <div className="p-5 rounded-3xl bg-zinc-900/30 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-2xl bg-amber-500/10 flex items-center justify-center">
                    <Zap size={15} className="text-amber-400" fill="currentColor" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Community Hype</p>
                    <p className="text-zinc-600 text-xs mt-0.5">3 Hype-Votes pro Monat für deine Lieblingsreleases</p>
                  </div>
                </div>
                <Link to="/charts" className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 text-xs font-medium transition-colors">
                  Zu den Charts <ChevronRight size={12} />
                </Link>
              </div>

              {/* Quick links */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { to: "/profil/favoriten", icon: Heart, label: "Favoriten", sub: "Gespeicherte Releases", color: "text-rose-400", bg: "bg-rose-600/10" },
                  { to: "/profil/follows", icon: Bell, label: "Following", sub: "Gefolgte Artists", color: "text-zinc-400", bg: "bg-zinc-800/60" },
                ].map(({ to, icon: Icon, label, sub, color, bg }) => (
                  <Link key={to} to={to}
                    className="group flex flex-col gap-3 p-4 rounded-3xl bg-zinc-900/30 hover:bg-zinc-900/50 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] transition-all duration-200">
                    <div className={`w-9 h-9 rounded-2xl ${bg} flex items-center justify-center`}>
                      <Icon size={15} className={color} />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{label}</p>
                      <p className="text-zinc-600 text-xs mt-0.5">{sub}</p>
                    </div>
                  </Link>
                ))}
              </div>

            </div>
          )}

          {/* ── DATENSCHUTZ TAB ── */}
          {activeTab === "datenschutz" && (
            <div className="pb-16 space-y-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-700 mb-5 ml-1">{t("profile.privacy_tab_title")}</p>
                <div className="space-y-3">
                  {/* Profil öffentlich */}
                  <button
                    type="button"
                    onClick={() => { setProfilPublic(!profilPublic); if (profilPublic) setInMemberList(false); }}
                    className={`w-full flex items-start gap-4 p-4 rounded-2xl border transition-all text-left ${profilPublic ? "border-rose-500/30 bg-rose-500/5" : "border-zinc-800 bg-zinc-900/30 hover:border-zinc-700"}`}
                    data-testid="toggle-profil-public"
                  >
                    <div className={`w-11 h-6 rounded-full flex-shrink-0 mt-0.5 transition-colors relative ${profilPublic ? "bg-rose-600" : "bg-zinc-700"}`}>
                      <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${profilPublic ? "left-6" : "left-1"}`} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">{t("profile.public_profile")}</p>
                      <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">
                        {t("profile.public_profile_desc", { name: publicUser?.username })}
                      </p>
                    </div>
                  </button>

                  {/* In Mitgliederliste */}
                  <button
                    type="button"
                    onClick={() => profilPublic && setInMemberList(!inMemberList)}
                    disabled={!profilPublic}
                    className={`w-full flex items-start gap-4 p-4 rounded-2xl border transition-all text-left ${!profilPublic ? "opacity-40 cursor-not-allowed border-zinc-800 bg-zinc-900/20" : inMemberList ? "border-rose-500/30 bg-rose-500/5 cursor-pointer" : "border-zinc-800 bg-zinc-900/30 hover:border-zinc-700 cursor-pointer"}`}
                    data-testid="toggle-in-member-list"
                  >
                    <div className={`w-11 h-6 rounded-full flex-shrink-0 mt-0.5 transition-colors relative ${inMemberList && profilPublic ? "bg-rose-600" : "bg-zinc-700"}`}>
                      <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${inMemberList && profilPublic ? "left-6" : "left-1"}`} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">{t("profile.in_member_list")}</p>
                      <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">
                        {t("profile.in_member_list_desc")} · {t("profile.in_member_list_requires")}
                      </p>
                    </div>
                  </button>
                </div>

                <div className="flex items-center gap-3 mt-4">
                  <button
                    onClick={handlePrivacySave}
                    disabled={privacySaving}
                    data-testid="save-privacy-btn"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold transition-colors disabled:opacity-50"
                  >
                    {privacySaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                    {privacySaved ? t("common.saved") : t("common.save")}
                  </button>
                  {privacySaved && <span className="text-emerald-400 text-xs flex items-center gap-1"><Check size={11} /> {t("common.saved")}</span>}
                </div>
              </div>

              {/* DSGVO + Benachrichtigungen */}
              <div className="pt-2 space-y-6">

                {/* Benachrichtigungen */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-700 mb-4 ml-1">{t("profile.notif_settings_title")}</p>
                  <div className="space-y-2">
                    {[
                      { key: "new_release",      label: t("profile.notif_new_release"),    desc: t("profile.notif_new_release_desc") },
                      { key: "push_new_release", label: t("profile.notif_push_release"),   desc: t("profile.notif_push_release_desc") },
                      { key: "new_follower",     label: t("profile.notif_new_follower"),   desc: t("profile.notif_new_follower_desc") },
                    ].map(({ key, label, desc }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setNotifPrefs(p => ({ ...p, [key]: !p[key] }))}
                        className={`w-full flex items-start gap-4 p-4 rounded-2xl border transition-all text-left ${notifPrefs[key] ? "border-rose-500/30 bg-rose-500/5" : "border-zinc-800 bg-zinc-900/30 hover:border-zinc-700"}`}
                      >
                        <div className={`w-11 h-6 rounded-full flex-shrink-0 mt-0.5 transition-colors relative ${notifPrefs[key] ? "bg-rose-600" : "bg-zinc-700"}`}>
                          <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${notifPrefs[key] ? "left-6" : "left-1"}`} />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-white">{label}</p>
                          <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mt-4">
                    <button
                      onClick={handleNotifSave}
                      disabled={notifSaving}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold transition-colors disabled:opacity-50"
                    >
                      {notifSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                      {notifSaved ? "Gespeichert" : "Speichern"}
                    </button>
                    {notifSaved && <span className="text-emerald-400 text-xs flex items-center gap-1"><Check size={11} /> Gespeichert</span>}
                  </div>
                </div>

                {/* Account & DSGVO */}
                <div>
                  <div className="flex items-center justify-between px-4 py-3.5 rounded-2xl bg-zinc-900/20 hover:bg-zinc-900/40 transition-colors group">
                    <div>
                      <p className="text-zinc-300 text-sm font-medium">{t("profile.export_data")}</p>
                      <p className="text-zinc-600 text-xs mt-0.5">{t("profile.export_data_desc")}</p>
                    </div>
                    <button onClick={handleExportData} disabled={exportLoading} data-testid="export-data-btn"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-zinc-400 text-xs font-medium transition-all flex-shrink-0 ml-4">
                      {exportLoading ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                      Export
                    </button>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3.5 rounded-2xl bg-zinc-900/20 hover:bg-zinc-900/40 transition-colors">
                    <div>
                      <p className="text-red-400/80 text-sm font-medium">{t("profile.delete_account")}</p>
                      <p className="text-zinc-600 text-xs mt-0.5">{t("profile.delete_account_desc")}</p>
                    </div>
                    <button onClick={() => { setShowDeleteModal(true); setDeleteConfirmed(false); }} data-testid="delete-account-btn"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-red-500/70 hover:text-red-400 hover:bg-red-500/[0.06] text-xs font-medium transition-all flex-shrink-0 ml-4">
                      <Trash2 size={11} /> {t("profile.delete_account")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Artist Studio Tab wurde nach /artist-studio verschoben */}
        </div>

        {/* ── Delete Modal ── */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <div className="bg-zinc-950 rounded-3xl p-7 w-full max-w-md shadow-[0_32px_80px_rgba(0,0,0,0.8)] ring-1 ring-inset ring-white/[0.07]">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-10 h-10 rounded-2xl bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <AlertTriangle size={17} className="text-red-400" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-lg mb-1">{t("profile.delete_account")}?</h2>
                  <p className="text-zinc-500 text-sm leading-relaxed">{t("profile.delete_account_desc")}</p>
                </div>
              </div>
              <label className="flex items-start gap-3 cursor-pointer mb-6 ml-14 group">
                <div onClick={() => setDeleteConfirmed(c => !c)}
                  className={`w-4 h-4 mt-0.5 rounded-md flex-shrink-0 flex items-center justify-center transition-all duration-200 ring-1 ring-inset ${deleteConfirmed ? "bg-red-600 ring-red-600" : "ring-white/20 group-hover:ring-red-500/50"}`}>
                  {deleteConfirmed && <Check size={9} className="text-white" />}
                </div>
                <span className="text-zinc-500 text-xs leading-relaxed">
                  {t("profile.delete_confirm_label")}
                </span>
              </label>
              <div className="flex gap-3">
                <button onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-zinc-300 text-sm font-medium transition-all ring-1 ring-inset ring-white/[0.07]">
                  {t("common.cancel")}
                </button>
                <button onClick={handleDeleteAccount} disabled={!deleteConfirmed || deleteLoading} data-testid="confirm-delete-account-btn"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-500 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 hover:-translate-y-0.5 shadow-[0_4px_14px_rgba(220,38,38,0.25)]">
                  {deleteLoading ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  {t("profile.delete_confirm_btn")}
                </button>
              </div>
            </div>
          </div>
        )}

        <Footer />
      </div>
    </>
  );
}
